import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, BookOpen, CheckCircle2, Download, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { BROCHURE, PRICE_NOTE, SITE } from "@/config/site";
import { formatPrice } from "@/data/products";
import { API, quoteLink, track, whatsappLink } from "@/lib/site";
import { cn } from "@/lib/utils";
import { Container, Img, MonogramPlaceholder, QuoteButton, Reveal, SectionHeading, WhatsAppButton, WhatsAppIcon } from "./Primitives";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function ProductCard({ product }) {
  const price = formatPrice(product);
  const id = slug(product.name);
  const waText = `Hello KhozaLand.\n\nI would like a quotation for: ${product.name}.\n\nQuantity:\nPersonalisation / design:\nRequired date:`;
  return (
    <article className="kl-card group flex h-full flex-col overflow-hidden" data-testid={`product-card-${id}`}>
      <div className="bg-[#F7F7F7] p-3">
        {product.media ? (
          <div className="overflow-hidden rounded-[12px] border border-black/10 bg-white" style={{ aspectRatio: "4/3" }}>
            <Img media={product.media} sizes="(min-width:1024px) 30vw, (min-width:640px) 45vw, 92vw" className="kl-photo transition-transform duration-500 group-hover:scale-[1.03]" />
          </div>
        ) : (
          <MonogramPlaceholder className="!bg-transparent !p-0" />
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg font-semibold tracking-tight text-[#161616]">{product.name}</h3>
        <p className="mt-1.5 text-sm leading-6 text-black/65">{product.desc}</p>
        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Customisation options">
          {product.options.map((o) => (
            <li key={o} className="rounded-full border border-black/12 bg-white px-2.5 py-1 text-[11px] font-medium text-black/65">{o}</li>
          ))}
        </ul>
        <div className="mt-auto pt-5">
          <p className="text-base font-semibold text-[#161616]" data-testid={`product-card-${id}-price`}>{price || "Price on quotation"}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to={quoteLink({ category: product.category, product: product.name })} onClick={() => track("quote_cta_click", { label: product.name })} className="kl-btn kl-btn-dark h-11 px-3 text-[13px]" data-testid={`product-card-${id}-enquire-button`}>Enquire</Link>
            <a href={whatsappLink(product.whatsapp, waText)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { label: product.name })} className="kl-btn kl-btn-outline h-11 px-3 text-[13px]" data-testid={`product-card-${id}-whatsapp-button`} aria-label={`WhatsApp about ${product.name} (opens in a new tab)`}><WhatsAppIcon /> WhatsApp</a>
          </div>
        </div>
      </div>
    </article>
  );
}

export function ProductGrid({ products, title = "Products", eyebrow = "Customise", id = "products" }) {
  const hasPrice = products.some((p) => p.price);
  return (
    <section className="py-16 lg:py-24" aria-labelledby={`${id}-title`} data-testid="product-grid">
      <Container>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <SectionHeading eyebrow={eyebrow} title={title} id={`${id}-title`} />
          {hasPrice ? <p className="max-w-sm text-xs leading-5 text-black/55" data-testid="price-note">{PRICE_NOTE}</p> : null}
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 70}><ProductCard product={p} /></Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

export function FaqList({ faqs, testId = "faq-accordion" }) {
  return (
    <Accordion type="single" collapsible className="w-full border-t border-black/10" data-testid={testId}>
      {faqs.map((f) => (
        <AccordionItem key={f.id} value={f.id} className="border-black/10">
          <AccordionTrigger className="py-5 text-left text-base font-semibold text-[#161616] hover:no-underline sm:text-[17px]" data-testid={`faq-trigger-${f.id}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="pb-5 pr-6 text-[15px] leading-7 text-black/70">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function FaqSection({ faqs, title = "Questions, answered", eyebrow = "FAQs" }) {
  return (
    <section className="bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="faq-title">
      <Container className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionHeading eyebrow={eyebrow} title={title} id="faq-title" />
          <Link to="/faqs" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold" data-testid="faq-view-all-link"><span className="kl-underline">View all FAQs</span> <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
        <div className="lg:col-span-8"><FaqList faqs={faqs} /></div>
      </Container>
    </section>
  );
}

const STEPS = [
  { n: "01", t: "Choose your product", d: "Browse clothing, gifts, printing and cultural products — or tell us your idea." },
  { n: "02", t: "Share your requirements", d: "Send your design or logo, quantities, sizes, colours and required date." },
  { n: "03", t: "Receive your quotation", d: "KhozaLand prepares a quote based on your requirements, including deposit terms." },
  { n: "04", t: "Confirm your order", d: "Approve the quotation to confirm. Production starts once your order is confirmed." },
];

export function HowToOrder({ dark = false }) {
  return (
    <section className={cn("py-16 lg:py-24", dark ? "bg-[#161616] text-white" : "bg-white")} aria-labelledby="how-title" data-testid="how-to-order">
      <Container>
        <SectionHeading eyebrow="How to order" title="Four simple steps to your custom order" lead="Submit your designs, quantities and customisation requirements — we'll take it from there." light={dark} id="how-title" />
        <ol className="mt-12 grid gap-px overflow-hidden rounded-[18px] border sm:grid-cols-2 lg:grid-cols-4" style={{ borderColor: dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)", background: dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)" }}>
          {STEPS.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 80} className={cn("p-7", dark ? "bg-[#161616]" : "bg-white")}>
              <span className={cn("font-display text-4xl", dark ? "text-white/35" : "text-black/25")}>{s.n}</span>
              <h3 className="mt-6 text-lg font-semibold">{s.t}</h3>
              <p className={cn("mt-2 text-sm leading-6", dark ? "text-white/65" : "text-black/65")}>{s.d}</p>
            </Reveal>
          ))}
        </ol>
      </Container>
    </section>
  );
}

export function FinalCta({ title = "Ready to Bring Your Idea to Life?", text = "Tell KhozaLand what you need, and let us help you create something personal and meaningful.", category, messageKey = "general", primaryLabel = "Request a Quote" }) {
  return (
    <section className="bg-[#161616] text-white" aria-labelledby="final-cta-title" data-testid="final-cta">
      <Container className="grid gap-10 py-16 lg:grid-cols-12 lg:items-end lg:py-24">
        <div className="lg:col-span-8">
          <p className="kl-eyebrow text-white/55">{SITE.motto}</p>
          <h2 id="final-cta-title" className="kl-h2 mt-4 text-white">{title}</h2>
          <p className="mt-4 max-w-xl text-base leading-7 text-white/70 sm:text-lg">{text}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:flex-col">
          <QuoteButton label={primaryLabel} category={category} variant="light" className="h-12" testId="final-cta-quote-button" />
          <WhatsAppButton messageKey={messageKey} variant="ghost-light" className="h-12" testId="final-cta-whatsapp-button" />
        </div>
      </Container>
    </section>
  );
}

export function CatalogueRequest({ compact = false }) {
  const [v, setV] = useState({ name: "", email: "", phone: "", interest: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => { setV((p) => ({ ...p, [k]: e.target.value })); setErrors((p) => ({ ...p, [k]: undefined })); };

  if (BROCHURE.url) {
    return (
      <a href={BROCHURE.url} download onClick={() => track("brochure_download", { label: "brochure" })} className="kl-btn kl-btn-dark" data-testid="brochure-download-button"><Download className="h-4 w-4" /> {BROCHURE.label}</a>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (v.name.trim().length < 2) errs.name = "Please enter your name";
    if (!v.email.trim() && !v.phone.trim()) errs.email = "Add an email or WhatsApp number";
    if (v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) errs.email = "Please enter a valid email address";
    if (!v.consent) errs.consent = "Please agree to the privacy notice";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus("submitting");
    try {
      const payload = { name: v.name.trim(), email: v.email.trim() || null, phone: v.phone.trim() || null, interest: v.interest.trim() || null, consent: true, website: v.website, source_page: window.location.pathname };
      const { data } = await axios.post(`${API}/catalogue-requests`, payload);
      if (!data.success) throw new Error("failed");
      setStatus("success");
      track("brochure_request", { label: "catalogue" });
      toast.success("Catalogue request received.");
    } catch (err) {
      setStatus("idle");
      const res = err.response;
      if (res && res.data && res.data.errors) setErrors(res.data.errors);
      toast.error((res && res.data && typeof res.data.detail === "string" && res.data.detail) || "We couldn't send your request. Please try again.");
    }
  };

  return (
    <section className={cn("border-y border-black/10 bg-white", compact ? "py-12" : "py-16 lg:py-20")} aria-labelledby="catalogue-title" data-testid="catalogue-request">
      <Container className="grid gap-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-5">
          <BookOpen className="h-7 w-7" aria-hidden="true" />
          <h2 id="catalogue-title" className="kl-h3 mt-4">Request Our Product Catalogue</h2>
          <p className="mt-3 text-sm leading-6 text-black/65">Want an overview of our products, services and quotation process? Leave your details and KhozaLand will send you our catalogue as soon as it's available.</p>
        </div>
        <div className="lg:col-span-7">
          {status === "success" ? (
            <div className="flex items-start gap-3 rounded-[14px] border border-black/10 bg-[#F7F7F7] p-5" role="status" data-testid="catalogue-request-success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <p className="text-sm leading-6">Thank you — your catalogue request has been saved. We'll be in touch using the details you provided.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="grid gap-3 sm:grid-cols-2" data-testid="catalogue-request-form">
              <div className="space-y-1.5">
                <Label htmlFor="cat-name" className="text-sm font-semibold">Name *</Label>
                <Input id="cat-name" value={v.name} onChange={set("name")} autoComplete="name" className="kl-input" aria-invalid={Boolean(errors.name) || undefined} data-testid="catalogue-name-input" />
                {errors.name ? <p role="alert" className="text-xs text-red-700">{errors.name}</p> : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cat-interest" className="text-sm font-semibold">Interested in <span className="font-normal text-black/45">(optional)</span></Label>
                <Input id="cat-interest" value={v.interest} onChange={set("interest")} placeholder="e.g. corporate gifts" className="kl-input" data-testid="catalogue-interest-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cat-email" className="text-sm font-semibold">Email</Label>
                <Input id="cat-email" type="email" value={v.email} onChange={set("email")} autoComplete="email" className="kl-input" aria-invalid={Boolean(errors.email) || undefined} data-testid="catalogue-email-input" />
                {errors.email ? <p role="alert" className="text-xs text-red-700">{errors.email}</p> : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cat-phone" className="text-sm font-semibold">or WhatsApp number</Label>
                <Input id="cat-phone" type="tel" value={v.phone} onChange={set("phone")} autoComplete="tel" className="kl-input" aria-invalid={Boolean(errors.phone) || undefined} data-testid="catalogue-phone-input" />
                {errors.phone ? <p role="alert" className="text-xs text-red-700">{errors.phone}</p> : null}
              </div>
              <div className="absolute -left-[9999px]" aria-hidden="true"><input tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} /></div>
              <div className="flex items-start gap-3 sm:col-span-2">
                <Checkbox id="cat-consent" checked={v.consent} onCheckedChange={(c) => { setV((p) => ({ ...p, consent: Boolean(c) })); setErrors((p) => ({ ...p, consent: undefined })); }} className="mt-0.5 h-5 w-5 rounded-[6px]" data-testid="catalogue-consent-checkbox" />
                <Label htmlFor="cat-consent" className="text-sm font-normal leading-6 text-black/70">I agree that KhozaLand may contact me about this request (<Link to="/privacy-policy" className="underline underline-offset-4">Privacy Policy</Link>).</Label>
              </div>
              {errors.consent ? <p role="alert" className="text-xs text-red-700 sm:col-span-2">{errors.consent}</p> : null}
              <div className="sm:col-span-2">
                <button type="submit" disabled={status === "submitting"} onClick={() => track("catalogue_cta_click", { label: "catalogue_form" })} className="kl-btn kl-btn-dark h-12 px-6 disabled:opacity-70" data-testid="catalogue-submit-button">
                  {status === "submitting" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Request Our Product Catalogue"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Container>
    </section>
  );
}

export function RelatedLinks({ links, titles }) {
  return (
    <nav aria-label="Related pages" className="py-14" data-testid="related-links">
      <Container>
        <p className="kl-eyebrow text-black/55">Explore more</p>
        <ul className="mt-5 grid gap-3 sm:grid-cols-3">
          {links.map((to) => (
            <li key={to}>
              <Link to={to} className="kl-card group flex items-center justify-between px-5 py-5" data-testid={`related-link-${to.replace(/\//g, "")}`}>
                <span className="font-semibold">{titles[to]}</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </Container>
    </nav>
  );
}
